import React, { useEffect, useState } from 'react';
import { ParentContainer } from '../../styles';
import { StepHeader, CommonButton, ModalFactory, BankDetailsCard } from '@Components';
import {
  ACKNOWLEDGEMENT_SCREEN,
  FUND_TRANSFER_MODAL,
  screenSettings,
  totalSteps
} from '../../utils/constants';
import { CLOSE_POPUP_ICON, DOCUMENT_ID_ICON_DUAL_TONE, INFO_NEW_ICON } from '@Images';
import { NF_SCREENS } from '../../root/screenConstants';
import { BankTransferWrapper, WarningWrapper, Wrapper, TextInfo } from './Style';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import { connect } from 'react-redux';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import {
  changeNiumForexScreen,
  getNiumForexPayInBanks,
  getNiumForexTransactionDetails
} from '@Redux/actions/nium-forex';
import {
  IAcknowledgementScreen,
  IPayInBankDetails,
  IVerifyPaymentTransaction
} from '../../utils/interface';
import { IStore } from '../../../../store.type';
import { FLOW_TYPE_TRANSACTION, NF_vKYC } from '@Utils/constants/eventsConstants';
import { VERIFIED } from '../../../../utils/constants';
import { logEvent } from '../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../utils/events/eventsConstants';

const AcknowledgementScreen = (props: IAcknowledgementScreen): React.ReactElement => {
  const {
    getNiumForexPayInBanks,
    countryCode,
    sourceCurrency,
    transactionId,
    dispatch,
    isReferral,
    videoKycStatus
  } = props;
  const stepperData = screenSettings[NF_SCREENS.acknowledgement as keyof typeof screenSettings];
  const [details, setDetails] = useState<IPayInBankDetails[]>([]);
  const [transactionData, setTransactionData] = useState<IVerifyPaymentTransaction>();

  const selectedDetails = isReferral ? details[0] : details[1];

  const bankData = [
    {
      key: FUND_TRANSFER_MODAL.accName,
      value: selectedDetails?.accountName
    },
    {
      key: FUND_TRANSFER_MODAL.accNumber,
      value: selectedDetails?.accountNumber
    },
    {
      key: FUND_TRANSFER_MODAL.ifsc,
      value: selectedDetails?.routingCode?.IFSC
    },
    {
      key: FUND_TRANSFER_MODAL.bankName,
      value: selectedDetails?.bankName
    },
    {
      key: FUND_TRANSFER_MODAL.branch,
      value: selectedDetails?.address
    },
    {
      key: FUND_TRANSFER_MODAL.txnNumber,
      value: transactionData?.transaction_number || '',
      extraInfo: FUND_TRANSFER_MODAL.txnDetails
    }
  ];

  const getBankData = async () => {
    dispatch(activateLoader());
    const response = await dispatch(getNiumForexPayInBanks(countryCode, sourceCurrency));
    if (response?.data?.success) {
      setDetails(response?.data?.data?.payinBankDetails);
    }
    dispatch(disableLoader());
  };

  const getTransactionData = async () => {
    dispatch(activateLoader());
    const response = await dispatch(getNiumForexTransactionDetails(transactionId));
    if (response?.data?.success) {
      setTransactionData(response?.data?.data);
    }
    dispatch(disableLoader());
  };

  useEffect(() => {
    getBankData();
    getTransactionData();
    //eslint-disable-next-line
  }, []);

  const handleClick = async () => {
    logEvent(NF_vKYC, FLOW_TYPE_TRANSACTION, { pageName: 'Acknowledgement' }, LOG_TYPE.CLEVER_TAP);
    dispatch(
      changeNiumForexScreen(
        videoKycStatus === VERIFIED ? NF_SCREENS.send_money : NF_SCREENS.video_kyc
      )
    );
  };

  const onCancelPayment = async () => {
    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal
          onContinue={() => onClose(true)}
          postPan={true}
          onConfirm={() => {
            dispatch(changeNiumForexScreen(NF_SCREENS.send_money));
            onClose(true);
          }}
        />
      ),
      'center-modal insta-modal',
      true
    );
  };

  return (
    <ParentContainer padding={[48, 20, 48, 20]}>
      <div className="navigationContainer">
        <img className="closeButton" src={CLOSE_POPUP_ICON} alt="close" onClick={onCancelPayment} />
      </div>
      <StepHeader
        heading={ACKNOWLEDGEMENT_SCREEN.heading}
        headerLogo={DOCUMENT_ID_ICON_DUAL_TONE}
        stepNumber={stepperData?.stepNumber}
        totalSteps={totalSteps}
        subHeading={''}
      />
      <Wrapper>
        <WarningWrapper>
          <img src={INFO_NEW_ICON} alt="info" className="info-image" />
          <div className="instructions">
            Please add Nium Forex as a beneficiary to your&nbsp;
            <span className="styled-word">personal savings </span>bank account. If you use someone
            else&apos;s account, the transfer will be cancelled and refunded.
          </div>
        </WarningWrapper>
        <BankTransferWrapper>{ACKNOWLEDGEMENT_SCREEN.bankTransferDetails}</BankTransferWrapper>
        <BankDetailsCard dataArray={bankData} />
        <TextInfo>{isReferral ? ACKNOWLEDGEMENT_SCREEN.videoVerificationText : ''}</TextInfo>
        <CommonButton
          text={
            videoKycStatus === VERIFIED
              ? ACKNOWLEDGEMENT_SCREEN.goToHome
              : ACKNOWLEDGEMENT_SCREEN.proceedButton
          }
          onClick={handleClick}
          width={450}
          height={46}
        />
      </Wrapper>
    </ParentContainer>
  );
};

function mapStateToProps(reduxState: IStore) {
  const {
    rootReducer: {
      userStatusReducer: { country_code: countryCode },
      niumForexReducer: {
        transactionDetails: { transactionId, sourceCurrency },
        userDetails: { video_kyc_status: videoKycStatus },
        isReferral
      }
    }
  } = reduxState;
  return {
    countryCode,
    transactionId,
    sourceCurrency,
    isReferral,
    videoKycStatus
  };
}

export default connect<any, any, any, IStore>(mapStateToProps, (dispatch?: any) => {
  return {
    dispatch,
    activateLoader,
    disableLoader,
    getNiumForexTransactionDetails,
    getNiumForexPayInBanks,
    changeNiumForexScreen
  };
})(AcknowledgementScreen);
